<template>
  <div class="bg-muted min-h-screen">
    <div class="container mx-auto px-4 py-12">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1 class="text-4xl font-bold text-foreground mb-4">
          Everything You Need to Win More Upwork Jobs
        </h1>
        <p class="text-muted-foreground max-w-3xl mx-auto text-lg mb-6">
          Applypa covers your whole Upwork routine: find clients worth bidding on, get alerted
          first, write the proposal, send it, and keep track of every application.
        </p>
        <ol class="flex flex-wrap justify-center gap-2 text-sm">
          <li v-for="step in steps" :key="step.key">
            <a :href="`#${step.key}`" class="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-border bg-card text-foreground font-semibold hover:bg-accent">
              <span class="text-primary tabular-nums">{{ step.num }}</span>{{ step.label }}
            </a>
          </li>
        </ol>
      </div>

      <!-- Workflow steps -->
      <section
        v-for="step in steps"
        :id="step.key"
        :key="step.key"
        class="max-w-5xl mx-auto mb-16 scroll-mt-20"
      >
        <p class="text-xs font-bold text-primary uppercase tracking-widest mb-2">Step {{ step.num }} · {{ step.label }}</p>
        <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-2">{{ step.title }}</h2>
        <p class="text-muted-foreground max-w-2xl mb-6">{{ step.lead }}</p>

        <!-- Step 1 visual: sample Upwork job with About the client -->
        <div v-if="step.key === 'find'" class="bg-card rounded-2xl shadow-lg border-2 border-primary/20 p-5 md:p-6 mb-6 grid md:grid-cols-2 gap-6 items-center">
          <div class="rounded-xl border border-border bg-background p-4 shadow-sm min-w-0">
            <div class="flex items-start gap-2">
              <img :src="upworkLogo" alt="Upwork" class="w-[18px] h-[18px] shrink-0 mt-0.5" />
              <p class="flex-1 min-w-0 text-[15px] font-bold leading-snug text-foreground">Virtual Assistant for Shopify Store, Email and Order Support</p>
            </div>
            <div class="mt-1.5 pl-[26px] flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>United States</span>
              <span>Hourly</span>
              <span class="font-semibold text-emerald-700 dark:text-emerald-400">$8–12 / hr</span>
              <span>Intermediate</span>
              <span>1 to 3 months</span>
            </div>
            <div class="mt-4 rounded-lg border border-border bg-card p-3.5">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <p class="text-sm font-bold text-foreground">About the client</p>
                <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400"><i class="fas fa-circle-check"></i>Payment verified</span>
              </div>
              <p class="mt-1.5 flex items-center gap-1.5 text-[13px]">
                <span class="text-amber-500 text-xs"><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star-half-stroke"></i></span>
                <span class="font-bold text-foreground">4.7</span><span class="text-muted-foreground">of 38 reviews</span>
              </p>
              <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div><dt class="font-bold text-foreground">$24.6K spent</dt><dd class="text-muted-foreground">34 hires</dd></div>
                <div><dt class="font-bold text-foreground">74% hire rate</dt><dd class="text-muted-foreground">Member since 2019</dd></div>
              </dl>
            </div>
            <p class="mt-3 text-[11px] text-muted-foreground">Sample job for illustration.</p>
          </div>
          <div>
            <p class="text-lg font-bold text-foreground mb-2">Know the client before you spend Connects</p>
            <p class="text-muted-foreground">
              Every Upwork job shows About the client up front: payment verified, rating and
              reviews, country, total spent, hires, hire rate and member since. Skip the
              posts from clients who never hire.
            </p>
          </div>
        </div>

        <div class="grid md:grid-cols-2 gap-6">
          <div
            v-for="f in step.features"
            :key="f.title"
            class="bg-card rounded-lg shadow-md p-6 md:p-7 flex flex-col"
          >
            <div class="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center mb-5">
              <i :class="f.icon" class="text-primary text-xl"></i>
            </div>
            <h3 class="text-xl font-bold text-foreground mb-2">{{ f.title }}</h3>
            <p class="text-muted-foreground mb-4 flex-1">{{ f.body }}</p>
            <p v-if="f.note" class="mb-4 text-xs font-semibold text-muted-foreground"><i class="fas fa-circle-info mr-1"></i>{{ f.note }}</p>
            <router-link
              v-if="f.to"
              :to="f.to"
              class="text-primary font-medium hover:text-primary-dark flex items-center"
            >
              {{ f.cta }} <i class="fas fa-arrow-right ml-2 text-sm"></i>
            </router-link>
          </div>
        </div>
      </section>

      <!-- Set up in minutes -->
      <section class="max-w-5xl mx-auto mb-16">
        <p class="text-xs font-bold text-primary uppercase tracking-widest mb-2">Getting started</p>
        <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-2">Set up in minutes</h2>
        <p class="text-muted-foreground max-w-2xl mb-6">Your proposals are only as good as what Applypa knows about you, so filling in your profile takes one step.</p>
        <div class="grid md:grid-cols-2 gap-6">
          <div v-for="f in setup" :key="f.title" class="bg-card rounded-lg shadow-md p-6 md:p-7">
            <div class="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center mb-5">
              <img v-if="f.upwork" :src="upworkLogo" alt="" class="w-7 h-7" />
              <i v-else :class="f.icon" class="text-primary text-xl"></i>
            </div>
            <h3 class="text-xl font-bold text-foreground mb-2">{{ f.title }}</h3>
            <p class="text-muted-foreground">{{ f.body }}</p>
          </div>
        </div>
      </section>

      <!-- Plans -->
      <section class="max-w-5xl mx-auto mb-8">
        <p class="text-xs font-bold text-primary uppercase tracking-widest mb-2">Plans</p>
        <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-2">What each plan includes</h2>
        <p class="text-muted-foreground max-w-2xl mb-6">Every new account starts with 14 days of full Premium access, no credit card. After that you move to Free, or pick a plan. Priced in pesos.</p>
        <div class="bg-card rounded-lg shadow-md overflow-x-auto">
          <table class="w-full min-w-[560px] text-sm text-left">
            <thead>
              <tr class="border-b border-border">
                <th scope="col" class="p-4 font-semibold text-muted-foreground">Feature</th>
                <th v-for="p in planColumns" :key="p.type" scope="col" class="p-4">
                  <span class="block font-bold text-foreground">{{ p.name }}</span>
                  <span class="block text-xs font-semibold text-muted-foreground">{{ p.price }} / month</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in planRows" :key="row.label" class="border-b border-border last:border-0">
                <th scope="row" class="p-4 font-semibold text-foreground">{{ row.label }}</th>
                <td v-for="(cell, i) in row.cells" :key="i" class="p-4 text-muted-foreground">
                  <i v-if="cell === true" class="fas fa-check text-primary" aria-label="Included"></i>
                  <span v-else-if="cell === false" class="text-muted-foreground/60">Not included</span>
                  <template v-else>{{ cell }}</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
          <router-link to="/register" class="inline-flex items-center gap-2 h-12 px-5 rounded-lg bg-primary text-primary-foreground font-bold hover:opacity-90">
            Start your free 14-day trial <i class="fas fa-arrow-right text-sm"></i>
          </router-link>
          <router-link to="/subscription/plans" class="text-primary font-medium hover:text-primary-dark">Compare all plans</router-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "vue";
import { useTheme } from "@/composables/useTheme";
import { sourceLogo } from "@/utils/sourceLogos";
import {
  resolvePlanFeatures,
  formatPeso,
  coverLettersCell,
  savedJobsCell,
  useSubscriptionPlans,
} from "@/composables/useSubscriptionPlans";

// Only features that are live for every user. OnlineJobs.ph-only features (Auto Apply,
// Direct Apply, inbox sync) are admin-gated and left out on purpose.
const STEPS = [
  {
    key: "find", num: "01", label: "Find",
    title: "Only the Upwork jobs worth your Connects",
    lead: "New Upwork jobs land in your feed all day, filtered to the work you actually want.",
    features: [
      { icon: "fas fa-sliders", title: "Saved filter presets", body: "Save a search for each kind of work, like VA gigs or social media, and switch between them in one tap. Drag tabs to reorder them.", to: "/job-filters", cta: "Try the filters" },
      { icon: "fas fa-magnifying-glass", title: "Precise filters", body: "Include or exclude keywords, search inside job descriptions, and filter by job type, pay per hour or month, and date posted.", to: "/job-filters", cta: "Browse Upwork jobs" },
      { icon: "fas fa-eye-slash", title: "Hide jobs you'll never take", body: "Mark a job Not interested and it disappears from your feed, so the list stays clean. Unhide it any time.", to: "/hidden-jobs", cta: "See hidden jobs" },
      { icon: "fas fa-bookmark", title: "Save jobs for later", body: "Bookmark jobs to bid on later and compare clients before you spend your Connects. Unlimited on every plan.", to: "/saved-jobs", cta: "View saved jobs" },
    ],
  },
  {
    key: "alerts", num: "02", label: "Get alerted",
    title: "Be one of the first to bid",
    lead: "Good Upwork jobs get dozens of proposals within hours, and US clients post while you're asleep.",
    features: [
      { icon: "fas fa-envelope", title: "Email alerts", body: "New jobs matching your saved filters, sent to your inbox instantly, daily or weekly at the hour you pick.", note: "Free plan: weekly digest. Paid plans: instant, daily or weekly.", to: "/notifications", cta: "Set up alerts" },
      { icon: "fab fa-telegram", title: "Telegram and Discord alerts", body: "Get matches on your phone within minutes of posting, in a Telegram chat or channel, or as a Discord DM.", note: "Basic and Premium plans.", to: "/notifications", cta: "Connect Telegram or Discord" },
    ],
  },
  {
    key: "write", num: "03", label: "Write",
    title: "A proposal for this client, in one click",
    lead: "Applypa reads the Upwork job post, your profile and your resume, then writes a proposal that answers what this client asked for.",
    features: [
      { icon: "fas fa-wand-magic-sparkles", title: "AI proposals", body: "Generate a proposal from any job in your feed. Change the tone, length and focus, or regenerate for a fresh take.", to: "/job-filters", cta: "Write your first proposal" },
      { icon: "fas fa-comments", title: "Chat to revise", body: "Ask for changes in plain words, like \"shorter\" or \"mention my Shopify experience\", and the proposal updates in place. Copy it when it's ready.", to: "/job-filters", cta: "Try it on a job" },
      { icon: "fas fa-pen-ruler", title: "AI Settings", body: "Teach Applypa how you write: your background, proposal strategy, writing style and what to emphasise. Preview the result on a real job from your filters.", to: "/ai-settings", cta: "Open AI Settings" },
    ],
  },
  {
    key: "send", num: "04", label: "Send",
    title: "From Applypa to Upwork's form, no copy-paste",
    lead: "The Applypa Chrome extension works on the Upwork job page you already have open.",
    features: [
      { icon: "fab fa-chrome", title: "Insert the proposal on Upwork", body: "Open any Upwork job, click Apply with Applypa, and the extension takes you to the application form and fills in your cover letter. It works on jobs Applypa hasn't added to your feed yet, too." },
      { icon: "fas fa-list-check", title: "Autofill screening questions", body: "The extension answers Upwork's extra application questions from your profile and resume. Anything it can't answer is left blank for you. It doesn't use your AI credits." },
      { icon: "fas fa-hand-pointer", title: "You always click submit", body: "The extension never clicks Send and never touches your rate or boost, so it never spends your Connects. We never ask for your Upwork password." },
    ],
  },
  {
    key: "track", num: "05", label: "Track",
    title: "Know what's working",
    lead: "Every proposal you send is logged, so you can see which bids turn into interviews.",
    features: [
      { icon: "fas fa-table-list", title: "Applications tracker", body: "After you open a job on Upwork, Applypa asks if you applied and logs it. Move each application from Applied to Reviewing, Interview, Offer or Hired, and see the proposal you sent.", to: "/applications", cta: "See your applications" },
      { icon: "fas fa-chart-line", title: "Dashboard", body: "New jobs today, applications this week, your pipeline and interview rate, plus how many AI credits and filter presets you've used.", to: "/dashboard", cta: "Open your dashboard" },
    ],
  },
];

const SETUP = [
  { upwork: true, title: "Import your Upwork profile", body: "During setup, the Chrome extension reads your own Upwork profile and fills in your Applypa profile for you, including Job Success, hours and earnings." },
  { icon: "fas fa-file-arrow-up", title: "Or upload your resume", body: "Upload a PDF and Applypa pulls out your skills and experience, so every proposal is written from your real work history." },
];

export default {
  name: "FeaturesPage",
  setup() {
    const { theme } = useTheme();
    const upworkLogo = computed(() => sourceLogo("upwork", theme.value));

    // Plan limits come from the same API as the pricing page, so they can't go stale.
    const { fetchSubscriptionPlans, findPlan } = useSubscriptionPlans();
    onMounted(fetchSubscriptionPlans);
    const PLAN_TYPES = [
      { type: "free", name: "Free" },
      { type: "basic", name: "Basic" },
      { type: "premium", name: "Premium" },
    ];
    const planFeatures = computed(() =>
      PLAN_TYPES.map((p) => ({ ...p, f: resolvePlanFeatures(findPlan(p.type, 30), p.type) })),
    );
    const planColumns = computed(() =>
      planFeatures.value.map((p) => ({ type: p.type, name: p.name, price: formatPeso(p.f.price) })),
    );
    const planRows = computed(() => {
      const cols = planFeatures.value;
      return [
        { label: "Upwork job feed with About the client", cells: cols.map(() => true) },
        { label: "AI proposals and cover letters", cells: cols.map((p) => coverLettersCell(p.f)) },
        { label: "Saved filter presets", cells: cols.map((p) => String(p.f.max_job_filter_presets)) },
        { label: "Email alerts", cells: cols.map((p) => (p.type === "free" ? "Weekly" : "Instant, daily or weekly")) },
        { label: "Telegram and Discord alerts", cells: cols.map((p) => !!p.f.has_telegram_alerts) },
        { label: "Chrome extension for Upwork", cells: cols.map(() => true) },
        { label: "Saved jobs", cells: cols.map((p) => savedJobsCell(p.f)) },
        { label: "Applications tracker and dashboard", cells: cols.map(() => true) },
      ];
    });

    return { steps: STEPS, setup: SETUP, upworkLogo, planColumns, planRows };
  },
};
</script>

<style scoped>
.bg-primary-light {
  background-color: hsl(var(--primary) / 0.1);
}
</style>
